<script setup lang="ts">
import { computed } from "vue";
import { MessageItem } from "../../../types/localMessage";
import BaseMessageContainer from "./BaseMessageContainer.vue";
import TextContent from "./TextContent.vue";
import ErrorSendIcon from "../icons/ErrorSendIcon.vue";
import { browserName } from "../../../utils/platform";

const props = defineProps<{
  message: MessageItem;
}>();
const file = computed(() => {
  if (props.message.content.file) return props.message.content.file;
  else
    return {
      filename: "",
      ext: "",
      localLink: "",
      preview: false,
      size: "",
      fileId: "",
    };
});

const downloadFile = () => {
  if (props.message.content.file) {
    if ("fileId" in props.message.content.file) {
      const link = `/api/file/download/${props.message.content.file.fileId}`;
      const a = document.createElement("a");
      a.href = link;
      if (["Firefox", "Safari"].includes(browserName)) {
        a.download = `${props.message.content.file.filename}${props.message.content.file.ext ? "." + props.message.content.file.ext : ""}`;
      } else {
        a.target = "__blank";
      }
      a.click();
    } else if (
      "localLink" in props.message.content.file &&
      "originalFile" in props.message.content.file
    ) {
      const url = URL.createObjectURL(props.message.content.file.originalFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = props.message.content.file.originalFile.name;
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>
<template>
  <BaseMessageContainer :message="message" class="file-message">
    <div class="file-message-wrapper">
      <div v-if="message.content.text" class="text font-with-emoji">
        <TextContent :content="message.content.text" />
      </div>
      <div class="file-block">
        <div class="file-card">
          <div class="icon" @click="downloadFile">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_1683_69570)">
                <path
                  d="M31.1333 16C31.1333 24.3579 24.3579 31.1333 16 31.1333C7.64209 31.1333 0.866667 24.3579 0.866667 16C0.866667 7.64209 7.64209 0.866667 16 0.866667C24.3579 0.866667 31.1333 7.64209 31.1333 16Z"
                  stroke="#A4A4A4" stroke-width="1.73333" stroke-linecap="round" stroke-linejoin="round" />
                <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M9.78769 8.78793C11.8379 6.73768 15.1621 6.73768 17.2123 8.78793L22.4449 14.0205L21.3842 15.0812L16.1517 9.84859C14.6872 8.38413 12.3128 8.38413 10.8483 9.84859C9.38388 11.3131 9.38388 13.6874 10.8483 15.1519L17.4521 21.7557C18.3936 22.6972 19.9201 22.6972 20.8616 21.7557C21.8031 20.8142 21.8031 19.2877 20.8616 18.3462L14.4853 11.9699C14.0668 11.5514 13.3882 11.5514 12.9697 11.9699C12.5511 12.3884 12.5511 13.067 12.9697 13.4856L17.9748 18.4907L16.9141 19.5513L11.909 14.5462C10.9047 13.5419 10.9047 11.9136 11.909 10.9093C12.9133 9.90493 14.5417 9.90493 15.546 10.9093L21.9223 17.2855C23.4495 18.8128 23.4495 21.289 21.9222 22.8163C20.395 24.3436 17.9187 24.3436 16.3915 22.8163L9.78769 16.2126C7.73744 14.1623 7.73744 10.8382 9.78769 8.78793Z"
                  fill="#A4A4A4" />
              </g>
              <defs>
                <clipPath id="clip0_1683_69570">
                  <rect width="32" height="32" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div class="content">
            <div class="filename">
              <span class="name">{{ file.filename }}</span>.<span class="ext">{{ file.ext }}</span>
            </div>
            <div class="filesize">{{ file.size }}</div>
          </div>
        </div>
        <div class="time">
          <ErrorSendIcon class="err-ico" v-if="message.local?.sendError" />
          {{ message.createAt }}
        </div>
      </div>
    </div>
  </BaseMessageContainer>
</template>
<style scoped>
.file-message-wrapper {
  width: 100%;
}

.file-message-wrapper .text {
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  white-space: pre-wrap;
  margin-bottom: 4px;
  word-break: break-all;
}

.file-block {
  display: flex;
  position: relative;
}

.file-block .time {
  position: absolute;
  bottom: 0;
  right: 0;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 19.07px;
  text-align: right;
  color: rgba(164, 164, 164, 1);
  user-select: none;
  display: flex;
  align-items: center;
}

.err-ico {
  flex-shrink: 0;
  margin-right: 4px;
}

.file-card {
  display: flex;
  height: 38px;
  width: 100%;
  position: relative;
  padding: 0 8px 0 0;
  gap: 8px;
}

.file-card .icon {
  display: flex;
  height: 100%;
  width: 32px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #ffffff;
  flex-grow: 1;
  width: 100px;
}

.filename {
  max-width: 90%;
  display: flex;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 400;
}

.filesize {
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 400;
  color: #A4A4A4;
}

.filename .name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
